import moment from "moment-timezone";

export const TIME_BLOCKS = [
  { id: "all", labelKey: "time_filter.all_times", startHour: 0, endHour: 24 },
  { id: "block_1", labelKey: "time_filter.block_1", startHour: 0, endHour: 4 },
  { id: "block_2", labelKey: "time_filter.block_2", startHour: 4, endHour: 8 },
  { id: "block_3", labelKey: "time_filter.block_3", startHour: 8, endHour: 12 },
  { id: "block_4", labelKey: "time_filter.block_4", startHour: 12, endHour: 16 },
  { id: "block_5", labelKey: "time_filter.block_5", startHour: 16, endHour: 20 },
  { id: "block_6", labelKey: "time_filter.block_6", startHour: 20, endHour: 24 },
];

/**
 * Checks if a leader (tutor) is available in the selected 4-hour time block.
 * Converts availability slots to the target timezone (defaults to learner's local timezone).
 *
 * @param {Object} leader The leader profile object
 * @param {string} selectedBlockId The ID of the time block (e.g. 'block_1', 'all')
 * @param {string} [userTimezone] Learner's local timezone
 * @returns {boolean} True if leader has at least 1 slot in the specified time block
 */
export function isLeaderAvailableInBlock(leader, selectedBlockId, userTimezone, customAvailabilities) {
  if (!selectedBlockId || selectedBlockId === "all") {
    return true;
  }

  const targetBlock = TIME_BLOCKS.find((b) => b.id === selectedBlockId);
  if (!targetBlock) return true;

  const { startHour, endHour } = targetBlock;
  const learnerTz = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Extract availabilities array from passed customAvailabilities or leader object
  const availabilities =
    customAvailabilities ||
    leader?.owner?.availabilities ||
    leader?.availabilities ||
    leader?.availability_time ||
    leader?.owner?.availability_time ||
    [];

  if (!Array.isArray(availabilities) || availabilities.length === 0) {
    return false;
  }

  return availabilities.some((slot) => {
    let localHour;

    if (slot.hour !== undefined && slot.hour !== null) {
      // Slot format: { day: 'Mon', hour: 14, minute: 0, timezone: 'America/Los_Angeles' }
      const slotTz = slot.timezone || leader?.owner?.timezone || leader?.timezone || learnerTz;
      const slotDay = slot.day || "Mon";
      const slotMinute = slot.minute || 0;

      try {
        const now = moment().tz(slotTz);
        const localTime = now
          .clone()
          .day(slotDay)
          .hour(slot.hour)
          .minute(slotMinute)
          .second(0)
          .tz(learnerTz);
        localHour = localTime.hour();
      } catch (err) {
        localHour = Number(slot.hour);
      }
    } else if (slot.time && typeof slot.time === "string") {
      // Slot format: { day: 'Mon', time: '14:00' }
      const timeParts = slot.time.split(":");
      const rawHour = parseInt(timeParts[0], 10);
      if (isNaN(rawHour)) return false;

      const slotTz = slot.timezone || leader?.owner?.timezone || leader?.timezone;
      if (slotTz) {
        try {
          const slotDay = slot.day || "Mon";
          const slotMinute = parseInt(timeParts[1] || "0", 10);
          const now = moment().tz(slotTz);
          const localTime = now
            .clone()
            .day(slotDay)
            .hour(rawHour)
            .minute(slotMinute)
            .second(0)
            .tz(learnerTz);
          localHour = localTime.hour();
        } catch (err) {
          localHour = rawHour;
        }
      } else {
        localHour = rawHour;
      }
    } else {
      return false;
    }

    return localHour >= startHour && localHour < endHour;
  });
}
